import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Agent } from '../types';

export type CharacterRuntimeState =
  | 'idle'
  | 'working'
  | 'waiting_on_owner'
  | 'stalled'
  | 'failed'
  | 'in_review';

function cleanState(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export function normalizeCharacterRuntimeState(
  value: unknown,
): CharacterRuntimeState {
  const state = cleanState(value);

  if (
    state === 'working' ||
    state === 'executing' ||
    state === 'running' ||
    state === 'claimed'
  ) {
    return 'working';
  }
  if (
    state === 'waiting_on_owner' ||
    state === 'need_you' ||
    state === 'needs_owner' ||
    state === 'owner_action'
  ) {
    return 'waiting_on_owner';
  }
  if (state === 'stalled' || state === 'blocked') return 'stalled';
  if (state === 'failed' || state === 'error') return 'failed';
  if (state === 'in_review' || state === 'review' || state === 'reviewing') {
    return 'in_review';
  }

  return 'idle';
}

export function characterRuntimeStateFromEvent(
  row: Record<string, unknown>,
): CharacterRuntimeState | null {
  const payload =
    row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)
      ? (row.payload as Record<string, unknown>)
      : {};

  for (const candidate of [
    payload.runtime_state,
    payload.lifecycle_stage,
    payload.status,
    payload.state,
  ]) {
    if (candidate) return normalizeCharacterRuntimeState(candidate);
  }

  const eventType = cleanState(row.event_type);
  if (!eventType) return null;

  if (eventType.includes('review')) return 'in_review';
  if (
    eventType.includes('waiting_on_owner') ||
    eventType.includes('needs_owner') ||
    eventType.includes('need_you')
  ) {
    return 'waiting_on_owner';
  }
  if (eventType.includes('fail') || eventType.includes('error')) return 'failed';
  if (eventType.includes('stall') || eventType.includes('block')) return 'stalled';
  if (
    eventType.includes('working') ||
    eventType.includes('started') ||
    eventType.includes('claimed') ||
    eventType.includes('executing')
  ) {
    return 'working';
  }
  if (
    eventType.includes('completed') ||
    eventType.includes('idle') ||
    eventType.includes('released')
  ) {
    return 'idle';
  }

  return null;
}

export function useCharacterRuntimeState(agent?: Agent): CharacterRuntimeState {
  const [state, setState] = useState<CharacterRuntimeState>(
    normalizeCharacterRuntimeState(agent?.status),
  );

  useEffect(() => {
    setState(normalizeCharacterRuntimeState(agent?.status));
  }, [agent?.id, agent?.status]);

  useEffect(() => {
    if (!agent?.id) return;

    const channel = supabase
      .channel('character-runtime:' + agent.id)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'agentopia_core_agents',
          filter: 'id=eq.' + agent.id,
        },
        payload => {
          const row = payload.new as Record<string, unknown>;
          setState(normalizeCharacterRuntimeState(row.status));
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'agentopia_core_events',
          filter: 'agent_id=eq.' + agent.id,
        },
        payload => {
          const next = characterRuntimeStateFromEvent(
            payload.new as Record<string, unknown>,
          );
          if (next) setState(next);
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [agent?.id]);

  return state;
}
